import React, { useState, useEffect, ReactNode } from 'react';
import { GitHubAuthContext, GitHubAuthContextType } from './GitHubAuthContextType';
import { GitHubTokenManager, enableDebugLogging } from '../utils/secureStorage';

interface GitHubAuthProviderProps {
  children: ReactNode;
}

// Enable verbose secure storage logging in development
if (import.meta.env.DEV) {
  enableDebugLogging(true);
}

export const GitHubAuthProvider: React.FC<GitHubAuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<{ login: string; name?: string; avatar_url: string; email?: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = async () => {
    try {
      await GitHubTokenManager.removeAccessToken();
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = await GitHubTokenManager.getAccessToken();
        console.info('[auth] token present?', { present: !!token, length: token?.length ?? 0 });
        if (!token) {
          setIsAuthenticated(false);
          setUser(null);
          return;
        }
        try {
          const response = await fetch('https://api.github.com/user', {
            headers: { Authorization: `token ${token}` },
          });
          
          if (!response.ok) {
            console.warn('[auth] /user failed', { status: response.status });
            setIsAuthenticated(false);
            setUser(null);
            return;
          }

          const userData = await response.json();
          console.info('[auth] /user success', { login: userData?.login });
          setIsAuthenticated(true);
          setUser({ 
            login: userData.login, 
            name: userData.name, 
            avatar_url: userData.avatar_url,
            email: userData.email 
          });
        } catch (err) {
          console.warn('[auth] /user failed', err);
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const value: GitHubAuthContextType = {
    isAuthenticated,
    user,
    logout,
    isLoading,
    accessToken: null,
    login: () => {},
  };

  return (
    <GitHubAuthContext.Provider value={value}>
      {children}
    </GitHubAuthContext.Provider>
  );
};
